// Redesign 2026 – sekce Předobjednávka (includes/template-parts/redesign/order.php): kalkulačka zapojená
// do Fluent Formu. Dva režimy podle markupu:
//  - výběr emise (LP /dluhopisy, Fluent Form 7 se selectem `delka_investice`),
//  - pevná emise (detail emise, Fluent Form 4; `data-rd-order-fixed="<id emise>"`, boční box vykreslil server).
//
// Formulář zůstává jednokrokový. JS jen:
//  - obalí pole „Investovaná částka“ krokovačem −/+ po 50 000 Kč, přidá rychlé volby a hint s počtem dluhopisů,
//  - živě plní boční box „Vaše volba“ (emise, částka, počet, splatnost, ISIN, rozpis výnosu),
//  - z tlačítka „Chci investovat“ na kartě (data-rd-issue) předvolí emisi v selectu (jen režim výběru),
//  - v Safari a Firefoxu nahradí systémový seznam selectu vlastním (./custom-select.js).
// Data emisí dodává PHP (includes/redesign/issues.php) jako JSON v <script data-rd-order-data>.
// Výpočty: ./order-calc.js (testy tests/redesign/order-calc.test.mjs).
import {
  STEP,
  snapAmount,
  pieces,
  piecesLabel,
  schedule,
  totals,
  formatCzk,
  formatPct,
  periodLabel,
  presetLabel,
} from './order-calc';
import enhanceSelect, { supportsStyledSelect } from './custom-select';

const DEFAULT_AMOUNT = 50000;
const PRESETS = [50000, 150000, 300000, 500000, 1000000, 2000000];

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) {
    node.className = className;
  }
  if (text !== undefined) {
    node.textContent = text;
  }
  return node;
}

function button(className, label, ariaLabel) {
  const btn = el('button', `rd-btn rd-btn--secondary ${className}`, label);
  btn.type = 'button';
  if (ariaLabel) {
    btn.setAttribute('aria-label', ariaLabel);
  }
  return btn;
}

/** Krokovač částky + rychlé volby + hint kolem číselného pole formuláře. */
function buildAmountControls(input, onChange) {
  input.min = String(STEP);
  input.step = String(STEP);
  input.inputMode = 'numeric';

  const wrap = el('div', 'rd-lp__amount');
  const dec = button('rd-lp__amount-btn', '−', `Snížit o ${formatCzk(STEP)}`);
  const inc = button('rd-lp__amount-btn', '+', `Zvýšit o ${formatCzk(STEP)}`);
  input.parentNode.insertBefore(wrap, input);
  wrap.append(dec, input, inc);

  const presets = el('div', 'rd-lp__amount-presets');
  PRESETS.forEach((value) => {
    const btn = button('rd-lp__amount-preset', presetLabel(value));
    btn.dataset.amount = String(value);
    btn.addEventListener('click', () => onChange(value));
    presets.appendChild(btn);
  });

  const hint = el('div', 'rd-lp__amount-hint');
  wrap.after(presets, hint);

  dec.addEventListener('click', () => onChange(Math.max(STEP, snapAmount(input.value) - STEP)));
  inc.addEventListener('click', () => onChange(snapAmount(input.value) + STEP));

  return { presets, hint };
}

export default function initRedesignOrder() {
  const root = document.querySelector('[data-rd-order]');
  if (!root) {
    return;
  }

  let issues = [];
  try {
    issues = JSON.parse(root.querySelector('script[data-rd-order-data]').textContent) || [];
  } catch (e) {
    return;
  }

  const fixedId = root.dataset.rdOrderFixed || '';
  const fixedIssue = fixedId ? issues.find((issue) => issue.id === fixedId) || null : null;
  const select = fixedId ? null : root.querySelector('select[name="delka_investice"]');
  const amountInput = root.querySelector('input[name="investovana_castka"]');
  if (!amountInput || (!select && !fixedIssue)) {
    return;
  }

  // Režim výběru: mapování emise ↔ option selectu podle přesného textu (viz `form_value` v issues.php).
  const issueByOptionValue = new Map();
  const optionByIssueId = new Map();
  if (select) {
    const options = Array.from(select.options);
    issues.forEach((issue) => {
      const option = options.find(
        (o) => o.value.trim() === issue.formValue || o.textContent.trim() === issue.formValue,
      );
      if (option) {
        issueByOptionValue.set(option.value, issue);
        optionByIssueId.set(issue.id, option);
      }
    });
  }

  const currentIssue = () => (select ? issueByOptionValue.get(select.value) || null : fixedIssue);

  const aside = root.querySelector('[data-rd-order-aside]');
  const slot = (name) => (aside ? aside.querySelector(`[data-rd-choice="${name}"]`) : null);

  function renderAside(amount, amountValid) {
    if (!aside) {
      return;
    }
    const issue = currentIssue();
    const calcBlock = slot('calc');
    const noCalc = slot('nocalc');

    slot('amount').textContent = formatCzk(amount);
    slot('pieces').textContent = piecesLabel(pieces(amount));

    if (!issue) {
      slot('short').textContent = 'Zatím nevybraná emise';
      slot('secured').textContent = 'Vyberte emisi ve formuláři, zobrazíme parametry a předpokládaný výnos.';
      slot('maturity').textContent = '—';
      slot('isin').textContent = '—';
      calcBlock.hidden = true;
      noCalc.hidden = true;
      return;
    }

    // V pevném režimu jsou název, zajištění, splatnost a ISIN vykreslené serverem (texty podle návrhu detailu).
    if (select) {
      slot('short').textContent = `${issue.short} · ${issue.tenor}`;
      slot('secured').textContent = issue.securedLabel;
      slot('maturity').textContent = issue.maturity;
      slot('isin').textContent = issue.isin;
    }

    const rows = schedule(amount, issue.calc);
    const sum = totals(amount, issue.calc);
    if (!rows.length || !sum) {
      calcBlock.hidden = true;
      noCalc.hidden = false;
      noCalc.textContent = issue.variableRate
        ? 'Výnos je navázaný na inflaci (meziroční inflace ČSÚ + 1 %), orientační propočet proto neuvádíme.'
        : 'Orientační propočet výnosu pro tuto emisi zatím není k dispozici.';
      return;
    }

    const tbody = slot('schedule');
    tbody.textContent = '';
    rows.forEach((row) => {
      const tr = el('tr');
      tr.append(
        el('td', 'rd-muted', periodLabel(row)),
        el('td', 'rd-lp__choice-num', formatPct(row.rate)),
        el('td', 'rd-lp__choice-num rd-lp__choice-strong', formatCzk(row.interest)),
      );
      tbody.appendChild(tr);
    });
    slot('total').textContent = formatCzk(sum.interest);
    slot('paid').textContent = formatCzk(sum.paid);
    calcBlock.hidden = false;
    noCalc.hidden = amountValid;
    if (!amountValid) {
      noCalc.textContent = `Propočet je pro nejbližší nižší násobek ${formatCzk(STEP)}.`;
    }
  }

  let controls = null;

  function render() {
    const raw = Number(String(amountInput.value).replace(/\s/g, ''));
    const amount = snapAmount(raw);
    const valid = Number.isFinite(raw) && raw >= STEP && raw % STEP === 0;

    if (controls) {
      controls.hint.classList.toggle('is-error', !valid);
      controls.hint.textContent = '';
      if (valid) {
        controls.hint.append(
          `Nominální hodnota jednoho dluhopisu je ${formatCzk(STEP)} — částka musí být jejím násobkem. Vychází to na `,
          el('strong', '', piecesLabel(pieces(amount))),
          '.',
        );
      } else {
        controls.hint.textContent = `Částka musí být násobkem ${formatCzk(STEP)}, nejméně ${formatCzk(STEP)}.`;
      }
      controls.presets.querySelectorAll('[data-amount]').forEach((btn) => {
        btn.classList.toggle('is-active', valid && Number(btn.dataset.amount) === raw);
      });
    }

    renderAside(amount, valid);
  }

  function setAmount(value) {
    amountInput.value = String(value);
    amountInput.dispatchEvent(new Event('input', { bubbles: true }));
    amountInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  controls = buildAmountControls(amountInput, setAmount);

  if (select) {
    select.addEventListener('change', render);
  }
  amountInput.addEventListener('input', render);
  amountInput.addEventListener('blur', () => {
    const raw = Number(amountInput.value);
    if (amountInput.value !== '' && Number.isFinite(raw) && (raw < STEP || raw % STEP !== 0)) {
      setAmount(snapAmount(raw));
    }
  });

  if (select) {
    // „Chci investovat“ na kartě emise → předvolit emisi; href="#objednavka" doscrolluje sám.
    document.querySelectorAll('[data-rd-issue]').forEach((link) => {
      link.addEventListener('click', () => {
        const option = optionByIssueId.get(link.dataset.rdIssue);
        if (option) {
          select.value = option.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });

    // Návrh: první emise předvybraná (žádné „– vyberte –“).
    if (select.value === '' && optionByIssueId.size) {
      select.value = optionByIssueId.values().next().value.value;
    }

    // „Nevíte, kterou zvolit? Porovnat podle zajištění“ pod selectem
    const selectGroup = select.closest('.ff-el-group');
    if (selectGroup && !selectGroup.querySelector('.rd-lp__field-help')) {
      const help = el('div', 'rd-lp__field-help rd-muted', 'Nevíte, kterou zvolit? ');
      const link = el('a', '', 'Porovnat podle zajištění');
      link.href = '#zajisteni';
      help.appendChild(link);
      selectGroup.appendChild(help);
    }

    // Safari (i všechny prohlížeče na iPhonu) a Firefox: vlastní seznam místo systémového, Chromium
    // má stejný vzhled čistě v CSS. Až po předvolbě výš – ta nastavuje hodnotu bez události `change`.
    if (!supportsStyledSelect()) {
      enhanceSelect(select);
    }
  }

  // Hint u tlačítka (návrh: „Vyplňte prosím všechna pole.“)
  const submitWrap = root.querySelector('.ff_submit_btn_wrapper');
  if (submitWrap && !submitWrap.querySelector('.rd-lp__submit-hint')) {
    const submitBtn = submitWrap.querySelector('.ff-btn-submit');
    if (submitBtn) {
      submitBtn.textContent = 'Odeslat předobjednávku';
      submitBtn.setAttribute('aria-label', 'Odeslat předobjednávku');
    }
    submitWrap.appendChild(el('span', 'rd-lp__submit-hint rd-muted', 'Vyplňte prosím všechna pole.'));
  }

  initSteps(root, select || amountInput);

  if (amountInput.value === '') {
    setAmount(DEFAULT_AMOUNT);
  }
  render();
}

/**
 * Lišta „1 · Emise a částka / 2 · Kdo investuje / 3 · Kontakt“ nad jednokrokovým formulářem:
 * klik doscrolluje na začátek dané části, aktivní krok se přepíná i podle fokusu ve formuláři.
 * `firstField` = pole, kterým první část začíná (select emise, v pevném režimu částka).
 */
function initSteps(root, firstField) {
  const nav = root.querySelector('[data-rd-steps]');
  if (!nav) {
    return;
  }
  const group = (name) => {
    const field = root.querySelector(`[name="${name}"]`);
    return field ? field.closest('.ff-el-group') : null;
  };
  const visible = (node) => node && node.offsetParent !== null;
  const firstVisible = (nodes) => nodes.find(visible) || null;

  // Cíle: emise = select; investor = přepínač FO/PO (hned pod ním jméno a příjmení);
  // kontakt = adresa (Trvalé bydliště / Sídlo firmy / Kontaktní adresa), pod ní telefon a e-mail.
  const targets = {
    emise: () => group(firstField.name),
    investor: () => firstVisible([group('input_radio'), group('input_name'), group('input_company_name')]),
    kontakt: () =>
      firstVisible([
        root.querySelector('.ff-el-section-break'),
        group('company_registered_office'),
        group('contact_address'),
        group('input_phone'),
      ]),
  };
  const order = ['emise', 'investor', 'kontakt'];
  const buttons = Array.from(nav.querySelectorAll('[data-rd-step]'));

  function setActive(step) {
    buttons.forEach((btn) => {
      const active = btn.dataset.rdStep === step;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  }

  function headerOffset() {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--rd-header-h');
    return (parseInt(raw, 10) || 110) + 24;
  }

  buttons.forEach((btn) => {
    btn.addEventListener('click', () => {
      const step = btn.dataset.rdStep;
      const target = targets[step] ? targets[step]() : null;
      setActive(step);
      if (target) {
        const top = target.getBoundingClientRect().top + window.scrollY - headerOffset();
        window.scrollTo({ top, behavior: 'smooth' });
        const focusable = target.querySelector('select, input:not([type="hidden"]), textarea');
        if (focusable) {
          focusable.focus({ preventScroll: true });
        }
      }
    });
  });

  // Aktivní krok podle toho, kde uživatel právě vyplňuje.
  root.addEventListener('focusin', (event) => {
    const field = event.target.closest('.ff-el-group');
    if (!field) {
      return;
    }
    const kontaktStart = targets.kontakt();
    const investorStart = targets.investor();
    const pos = (node) => (node ? node.getBoundingClientRect().top + window.scrollY : Infinity);
    const y = pos(field);
    let step = order[0];
    if (kontaktStart && y >= pos(kontaktStart)) {
      step = 'kontakt';
    } else if (investorStart && y >= pos(investorStart)) {
      step = 'investor';
    }
    setActive(step);
  });
}
